import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarCheck,
  CreditCard,
  Dumbbell,
  Target,
  TrendingUp,
  AlertCircle,
  Loader2,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import api from "../../lib/api";

function StatCard({ icon: Icon, label, value, sub, color = "text-primary" }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-3 sm:p-5">
        <div className="flex items-start gap-2 justify-between">
          <div className="w-full">
            <div className="flex items-center justify-between gap-1">
              <p className="text-xs font-semibold sm:text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5 truncate">
                {label}
              </p>
              <div
                className={`flex items-center justify-center shrink-0 ${color}`}
              >
                <Icon className="h-4 w-4 sm:h-5 sm:w-5 mb-1" />
              </div>
            </div>
            <p
              className={`text-base sm:text-xl font-black font-heading line-clamp-2 break-words ${color}`}
            >
              {value}
            </p>

            {sub && (
              <p className="text-xs font-semibold sm:text-xs text-muted-foreground mt-0.5 line-clamp-2 break-words ">
                {sub}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const getEffectiveStatus = (status, endDate) => {
  if (!["Active", "Partial"].includes(status)) {
    return status;
  }
  if (!endDate) return status;

  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  const now = new Date();
  if (now > end) {
    return "Expired";
  }
  return status;
};

export default function MemberDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const member = JSON.parse(localStorage.getItem("member_data") || "{}");

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("member_token");
      if (!token) {
        navigate("/");
        return;
      }
      try {
        const res = await api.get("/member-portal/dashboard", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const dashboardData = res.data;
        if (dashboardData.currentMembership) {
          dashboardData.currentMembership.status = getEffectiveStatus(
            dashboardData.currentMembership.status,
            dashboardData.currentMembership.endDate,
          );
        }
        if (dashboardData.currentMemberships) {
          dashboardData.currentMemberships =
            dashboardData.currentMemberships.map((ms) => ({
              ...ms,
              status: getEffectiveStatus(ms.status, ms.endDate),
            }));
        }
        if (dashboardData.membershipExpiry) {
          dashboardData.membershipStatus = getEffectiveStatus(
            dashboardData.membershipStatus,
            dashboardData.membershipExpiry,
          );
        }
        setData(dashboardData);
      } catch (err) {
        if (err?.response?.status === 401) {
          navigate("/");
          return;
        }
        setError("Failed to load dashboard.");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading)
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );

  if (error)
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-muted-foreground">
        <AlertCircle className="h-8 w-8" />
        <p>{error}</p>
      </div>
    );

  const ms = data?.currentMembership;
  const statusColor =
    data?.membershipStatus === "Active" || data?.membershipStatus === "Partial"
      ? "text-green-500"
      : data?.membershipStatus === "Expired"
        ? "text-red-500"
        : "text-muted-foreground";

  return (
    <div className="space-y-4 pb-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-black font-heading uppercase tracking-wide">
          Dashboard
        </h1>
        <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">
          Welcome back
          <span className="text-primary font-heading">
            {" "}
            {member.name ? ` ${member.name}` : ""}
          </span>
          ! Here's your activity summary.
        </p>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          icon={CalendarCheck}
          label="Attendance"
          value={data?.attendanceThisMonth ?? 0}
          sub="this month"
        />
        <StatCard
          icon={CreditCard}
          label="Membership"
          value={
            data?.currentMemberships && data.currentMemberships.length > 1
              ? `${data.currentMemberships.length} Plans`
              : ms
                ? ms.packageName
                : "None"
          }
          sub={
            data?.currentMemberships && data.currentMemberships.length > 1
              ? "Multiple active"
              : ms
                ? `${ms.status} • ${ms.daysRemaining}d left`
                : "No active plan"
          }
          color={statusColor}
        />
        <StatCard
          icon={Dumbbell}
          label="Workouts"
          value={data?.activeWorkouts ?? 0}
          sub="active routines"
        />
        <StatCard
          icon={Target}
          label="Goals"
          value={data?.activeGoals ?? 0}
          sub="in progress"
        />
      </div>

      {/* Current Membership progress */}
      {data?.currentMemberships && data.currentMemberships.length > 0
        ? data.currentMemberships.map((membershipItem, index) => (
            <Card key={index} className="bg-card border-border mt-4 sm:mt-6">
              <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
                <CardTitle className="text-xs sm:text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                  <span>
                    {data.currentMemberships.length > 1
                      ? `Membership (${index + 1})`
                      : "Current Membership"}
                  </span>
                  {data.currentMemberships.length > 1 && (
                    <span className="text-[9px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-sans font-medium lowercase">
                      {index === 0 ? "Primary" : "Additional"}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-base font-semibold text-xs sm:text-sm text-primary">
                    {membershipItem.packageName}
                  </span>
                  <span
                    className={`text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded-full ${
                      ["Active", "Partial"].includes(membershipItem.status)
                        ? "bg-green-500/10 text-green-500"
                        : "bg-red-500/10 text-red-500"
                    }`}
                  >
                    {membershipItem.status}
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-[10px] sm:text-xs text-muted-foreground">
                    <span>
                      {new Date(membershipItem.startDate).toLocaleDateString(
                        "en-IN",
                        { day: "numeric", month: "short", year: "2-digit" },
                      )}
                    </span>
                    <span className="font-semibold text-foreground">
                      {membershipItem.daysRemaining} days left
                    </span>
                    <span>
                      {new Date(membershipItem.endDate).toLocaleDateString(
                        "en-IN",
                        { day: "numeric", month: "short", year: "2-digit" },
                      )}
                    </span>
                  </div>
                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all"
                      style={{
                        width: `${Math.min(membershipItem.daysProgress, 100)}%`,
                      }}
                    />
                  </div>
                </div>
                {membershipItem.balanceDue > 0 && (
                  <div className="flex items-center gap-2 text-sm font-semibold text-amber-500 bg-amber-500/5 border border-amber-500/10 rounded-lg px-2.5 py-1.5">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      Balance due: ₹
                      {membershipItem.balanceDue.toLocaleString("en-IN")}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        : ms && (
            <Card className="bg-card border-border mt-4 sm:mt-6">
              <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
                <CardTitle className="text-[10px] sm:text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  Current Membership
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-xs sm:text-sm">
                    {ms.packageName}
                  </span>
                  <span
                    className={`text-[10px] sm:text-xs font-medium px-2 py-0.5 rounded-full ${
                      ["Active", "Partial"].includes(ms.status)
                        ? "bg-green-500/10 text-green-500"
                        : "bg-red-500/10 text-red-500"
                    }`}
                  >
                    {ms.status}
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-[10px] sm:text-xs text-muted-foreground">
                    <span>
                      {new Date(ms.startDate).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "2-digit",
                      })}
                    </span>
                    <span className="font-semibold text-foreground">
                      {ms.daysRemaining} days left
                    </span>
                    <span>
                      {new Date(ms.endDate).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "2-digit",
                      })}
                    </span>
                  </div>
                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all"
                      style={{ width: `${Math.min(ms.daysProgress, 100)}%` }}
                    />
                  </div>
                </div>
                {ms.balanceDue > 0 && (
                  <div className="flex items-center gap-2 text-xs text-amber-500 bg-amber-500/5 border border-amber-500/10 rounded-lg px-2.5 py-1.5">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      Balance due: ₹{ms.balanceDue.toLocaleString("en-IN")}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

      {/* Recent Attendance */}
      {data?.recentAttendance?.length > 0 && (
        <Card className="bg-card border-border mt-4 sm:mt-6">
          <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
            <CardTitle className="text-xs font-semibold sm:text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5" /> Recent Attendance
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0">
            <div className="space-y-1.5">
              {data.recentAttendance.map((r, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between py-1.5 border-b border-border last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    <span className="text-xs sm:text-sm">
                      {new Date(r.date).toLocaleDateString("en-IN", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      })}
                    </span>
                  </div>
                  <span className="text-[11px] text-muted-foreground">
                    {r.checkIn
                      ? new Date(r.checkIn).toLocaleTimeString("en-IN", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
